import { useCart } from "@ecommerce/store";
import { useSnackbar } from "notistack";
import { formatMoney } from "../../styles/liquidGlass";
import { Icon } from "../liquid/LiquidPrimitives";

interface QuantitySelectorProps {
  productId: string;
  variantId?: string;
  productName: string;
  productPrice: number;
  productImage: string;
  quantity: number;
  stock: number;
  onQuantityChange: (newQuantity: number) => void;
  minQuantity?: number;
  onAfterAddToCart?: () => void;
  attributes?: { name: string; value: string }[];
}

export default function QuantitySelector({
  productId,
  variantId,
  productName,
  productPrice,
  productImage,
  quantity,
  stock,
  onQuantityChange,
  minQuantity,
  onAfterAddToCart,
  attributes,
}: QuantitySelectorProps) {
  const { addToCart } = useCart();
  const { enqueueSnackbar } = useSnackbar();

  const effectiveMinQuantity = minQuantity ?? 1;
  const isPurchasable = stock >= effectiveMinQuantity;

  const handleAddToCart = () => {
    if (!isPurchasable) {
      enqueueSnackbar(`No hay stock suficiente para el minimo requerido (${effectiveMinQuantity})`, { variant: "warning" });
      return;
    }
    if (quantity < effectiveMinQuantity) {
      enqueueSnackbar(`La cantidad minima es ${effectiveMinQuantity}`, { variant: "warning" });
      return;
    }
    if (quantity > stock) {
      enqueueSnackbar(`No puede agregar ${quantity}, hay como maximo ${stock}`, { variant: "warning" });
      return;
    }
    if (!variantId) {
      enqueueSnackbar("Por favor selecciona una variante del producto", { variant: "warning" });
      return;
    }

    addToCart({
      idProduct: productId,
      idProductVariant: variantId,
      name: productName,
      price: productPrice,
      image: productImage,
      stock,
      quantity,
      attributes: attributes || [],
    });
    enqueueSnackbar("Producto agregado al carrito exitosamente!", { variant: "success" });
    onAfterAddToCart?.();
  };

  return (
    <div className="lg-strong" style={{ padding: "20px 24px", borderRadius: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div className="eyebrow">Cantidad</div>
        <span className="mono" style={{ fontSize: 11, color: "var(--ink-500)" }}>{stock} EN STOCK</span>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, padding: 4, borderRadius: 999, background: "rgba(10,10,10,0.06)" }}>
          <button
            type="button"
            onClick={() => onQuantityChange(Math.max(quantity - 1, effectiveMinQuantity))}
            disabled={quantity <= effectiveMinQuantity}
            style={{
              width: 38,
              height: 38,
              borderRadius: 999,
              border: "none",
              cursor: quantity <= effectiveMinQuantity ? "not-allowed" : "pointer",
              background: "white",
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: quantity <= effectiveMinQuantity ? .45 : 1,
            }}
          >
            <Icon.minus />
          </button>
          <span className="mono" style={{ minWidth: 50, textAlign: "center", fontSize: 16, fontWeight: 600 }}>{quantity}</span>
          <button
            type="button"
            onClick={() => onQuantityChange(Math.min(quantity + 1, stock))}
            disabled={!isPurchasable || quantity >= stock}
            style={{
              width: 38,
              height: 38,
              borderRadius: 999,
              border: "none",
              cursor: !isPurchasable || quantity >= stock ? "not-allowed" : "pointer",
              background: "white",
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: !isPurchasable || quantity >= stock ? .45 : 1,
            }}
          >
            <Icon.plus />
          </button>
        </div>

        <div style={{ textAlign: "right" }}>
          <div className="mono" style={{ fontSize: 11, color: "var(--ink-500)", letterSpacing: "0.1em" }}>SUBTOTAL</div>
          <div className="mono" style={{ fontSize: 22, fontWeight: 600, marginTop: 2 }}>{formatMoney(quantity * productPrice)}</div>
        </div>
      </div>

      {!!minQuantity && (
        <div
          style={{
            marginTop: 14,
            padding: "10px 14px",
            borderRadius: 12,
            background: "rgba(217, 194, 162, 0.25)",
            border: "1px solid rgba(217, 194, 162, 0.5)",
            fontSize: 12,
            color: "var(--ink-700)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Icon.spark />
          Minimo de compra <strong style={{ fontWeight: 600 }}>{minQuantity} unidades</strong>
        </div>
      )}

      <button
        type="button"
        className="btn-primary"
        onClick={handleAddToCart}
        disabled={!isPurchasable || !variantId}
        style={{
          width: "100%",
          marginTop: 14,
          padding: "20px 28px",
          fontSize: 15,
          opacity: !isPurchasable || !variantId ? .55 : 1,
          cursor: !isPurchasable || !variantId ? "not-allowed" : "pointer",
        }}
      >
        <Icon.bag /> Agregar al carrito
      </button>
    </div>
  );
}
